"use client";

import {
    END_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_MUTATION,
    START_CLAN_SPOILS_SESSION_MUTATION,
    START_DRINK_SESSION_MUTATION,
    START_SPOILS_SESSION_MUTATION,
    START_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_MUTATION,
} from "@/graphql/mutations/sessionMutations";
import {
    ACTIVE_CLAN_SPOILS_SESSION_QUERY_FOR_CLAN,
    ACTIVE_DRINK_SESSION_QUERY,
    ACTIVE_SPOILS_SESSION_QUERY,
    ACTIVE_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_QUERY,
} from "@/graphql/queries/sessionQueries";
import {
    ACTIVE_CLAN_SPOILS_SESSION_SUBSCRIPTION,
    ACTIVE_DRINK_SESSION_SUBSCRIPTION,
    ACTIVE_SPOILS_SESSION_SUBSCRIPTION,
    ACTIVE_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_SUBSCRIPTION,
} from "@/graphql/subscriptions/sessionSubscriptions";
import { classNamesExtended } from "@/helpers/common/classNames";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import React, { useState } from "react";
import FeedbackModal from "./FeedbackModal";
import VPInputModal from "./VPInputModal";

type ButtonConfig = {
    key: string;
    activeLabel: string;
    inactiveLabel: string;
    active: boolean;
    loading: boolean;
    onClick: () => void;
    successMessage?: React.ReactNode;
    errorMessage?: React.ReactNode;
    buttonColor: string;
    textColor: string;
};

const ModeratorStreamDeckPanel: React.FC = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalMessage, setModalMessage] = useState<React.ReactNode>("");
    const [modalIsError, setModalIsError] = useState(false);
    const [vpModalOpen, setVpModalOpen] = useState(false);
    const [spoilsVpModalOpen, setSpoilsVpModalOpen] = useState(false);
    const [clanSpoilsVpModalOpen, setClanSpoilsVpModalOpen] = useState(false);
    const [selectedClanTag, setSelectedClanTag] = useState<string>("");
    const [isStartingDrink, setIsStartingDrink] = useState(false);
    const [isStartingSpoils, setIsStartingSpoils] = useState(false);
    const [isStartingClanSpoils, setIsStartingClanSpoils] = useState(false);

    const showModal = (
        title: string,
        message: React.ReactNode,
        isError = false,
    ) => {
        setModalTitle(title);
        setModalMessage(message);
        setModalIsError(isError);
        setModalOpen(true);
    };

    const handleMutation = async ({
        mutationFn,
        refetchFn,
        successMessage,
        errorMessage,
    }: {
        mutationFn: () => Promise<any>;
        refetchFn: () => void;
        successMessage?: React.ReactNode;
        errorMessage?: React.ReactNode;
    }) => {
        try {
            await mutationFn();
            refetchFn();
            if (successMessage) {
                showModal("Skál!", successMessage, false);
            }
        } catch (err: any) {
            const fallback = errorMessage || "Oops, something went wrong.";
            showModal("Oops!", err?.message || fallback, true);
        }
    };

    const {
        data: wheelData,
        loading: wheelLoading,
        refetch: refetchWheel,
    } = useQuery(ACTIVE_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_SUBSCRIPTION, {
        onData: () => refetchWheel(),
    });
    const [startWheelOfHamingja, { loading: startWheelLoading }] = useMutation(
        START_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_MUTATION,
    );
    const [endWheelOfHamingja, { loading: endWheelLoading }] = useMutation(
        END_WHEEL_OF_HAMINGJA_REDEMPTION_SESSION_MUTATION,
    );

    const {
        data: drinkData,
        loading: drinkLoading,
        refetch: refetchDrink,
    } = useQuery(ACTIVE_DRINK_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_DRINK_SESSION_SUBSCRIPTION, {
        onData: () => refetchDrink(),
    });
    const [startDrinkSession] = useMutation(START_DRINK_SESSION_MUTATION);

    const {
        data: spoilsData,
        loading: spoilsLoading,
        refetch: refetchSpoils,
    } = useQuery(ACTIVE_SPOILS_SESSION_QUERY, {
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_SPOILS_SESSION_SUBSCRIPTION, {
        onData: () => refetchSpoils(),
    });
    const [startSpoilsSession] = useMutation(START_SPOILS_SESSION_MUTATION);

    const {
        data: clanSpoilsDataMFG,
        loading: clanSpoilsLoadingMFG,
        refetch: refetchClanSpoilsMFG,
    } = useQuery(ACTIVE_CLAN_SPOILS_SESSION_QUERY_FOR_CLAN, {
        variables: { clanTag: "MFG" },
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_CLAN_SPOILS_SESSION_SUBSCRIPTION, {
        variables: { clanTag: "MFG" },
        onData: () => refetchClanSpoilsMFG(),
    });
    const {
        data: clanSpoilsDataVHV,
        loading: clanSpoilsLoadingVHV,
        refetch: refetchClanSpoilsVHV,
    } = useQuery(ACTIVE_CLAN_SPOILS_SESSION_QUERY_FOR_CLAN, {
        variables: { clanTag: "VHV" },
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_CLAN_SPOILS_SESSION_SUBSCRIPTION, {
        variables: { clanTag: "VHV" },
        onData: () => refetchClanSpoilsVHV(),
    });
    const {
        data: clanSpoilsDataNSD,
        loading: clanSpoilsLoadingNSD,
        refetch: refetchClanSpoilsNSD,
    } = useQuery(ACTIVE_CLAN_SPOILS_SESSION_QUERY_FOR_CLAN, {
        variables: { clanTag: "NSD" },
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_CLAN_SPOILS_SESSION_SUBSCRIPTION, {
        variables: { clanTag: "NSD" },
        onData: () => refetchClanSpoilsNSD(),
    });
    const {
        data: clanSpoilsDataASW,
        loading: clanSpoilsLoadingASW,
        refetch: refetchClanSpoilsASW,
    } = useQuery(ACTIVE_CLAN_SPOILS_SESSION_QUERY_FOR_CLAN, {
        variables: { clanTag: "ASW" },
        fetchPolicy: "cache-and-network",
    });
    useSubscription(ACTIVE_CLAN_SPOILS_SESSION_SUBSCRIPTION, {
        variables: { clanTag: "ASW" },
        onData: () => refetchClanSpoilsASW(),
    });
    const [startClanSpoilsSession] = useMutation(
        START_CLAN_SPOILS_SESSION_MUTATION,
    );

    const handleStartDrinkSession = async (vpAmount: number) => {
        setIsStartingDrink(true);
        try {
            await startDrinkSession({ variables: { pointsReward: vpAmount } });
            refetchDrink();
            setVpModalOpen(false);
            showModal(
                "Skál!",
                `Drink session started with ${vpAmount} VP reward!`,
                false,
            );
        } catch (err: any) {
            showModal(
                "Oops!",
                err?.message ||
                    "There was an error starting the drink session.",
                true,
            );
        } finally {
            setIsStartingDrink(false);
        }
    };

    const handleStartSpoilsSession = async (vpAmount: number) => {
        setIsStartingSpoils(true);
        try {
            await startSpoilsSession({ variables: { pointsReward: vpAmount } });
            refetchSpoils();
            setSpoilsVpModalOpen(false);
            showModal(
                "Skál!",
                `Spoils session started with ${vpAmount} VP reward!`,
                false,
            );
        } catch (err: any) {
            showModal(
                "Oops!",
                err?.message ||
                    "There was an error starting the spoils session.",
                true,
            );
        } finally {
            setIsStartingSpoils(false);
        }
    };

    const handleStartClanSpoilsSession = async (vpAmount: number) => {
        setIsStartingClanSpoils(true);
        try {
            await startClanSpoilsSession({
                variables: { pointsReward: vpAmount, clanTag: selectedClanTag },
            });
            // Refetch the specific clan's query
            if (selectedClanTag === "MFG") refetchClanSpoilsMFG();
            else if (selectedClanTag === "VHV") refetchClanSpoilsVHV();
            else if (selectedClanTag === "NSD") refetchClanSpoilsNSD();
            else if (selectedClanTag === "ASW") refetchClanSpoilsASW();
            setClanSpoilsVpModalOpen(false);
            showModal(
                "Skál!",
                `Clan spoils session started for ${selectedClanTag} with ${vpAmount} VP reward!`,
                false,
            );
        } catch (err: any) {
            showModal(
                "Oops!",
                err?.message ||
                    "There was an error starting the clan spoils session.",
                true,
            );
        } finally {
            setIsStartingClanSpoils(false);
        }
    };

    const buttons: ButtonConfig[] = [
        {
            key: "startWheel",
            inactiveLabel: "START WOH SESSION",
            activeLabel: "START WOH SESSION!",
            active: !wheelData?.activeWheelOfHamingjaRedemptionSessionStatus
                ?.hasActiveSession,
            loading: wheelLoading || startWheelLoading,
            onClick: () =>
                handleMutation({
                    mutationFn: () => startWheelOfHamingja(),
                    refetchFn: refetchWheel,
                    successMessage: "Wheel of Hamingja session started!",
                    errorMessage:
                        "There was an error starting the Wheel of Hamingja session.",
                }),
            successMessage: "Wheel of Hamingja session started!",
            errorMessage:
                "There was an error starting the Wheel of Hamingja session.",
            buttonColor: "#FFCC33",
            textColor: "#1A855C",
        },
        {
            key: "endWheel",
            inactiveLabel: "END WOH SESSION",
            activeLabel: "END WOH SESSION!",
            active: wheelData?.activeWheelOfHamingjaRedemptionSessionStatus
                ?.hasActiveSession,
            loading: wheelLoading || endWheelLoading,
            onClick: () =>
                handleMutation({
                    mutationFn: () => endWheelOfHamingja(),
                    refetchFn: refetchWheel,
                    successMessage: "Wheel of Hamingja session ended!",
                    errorMessage:
                        "There was an error ending the Wheel of Hamingja session.",
                }),
            successMessage: "Wheel of Hamingja session ended!",
            errorMessage:
                "There was an error ending the Wheel of Hamingja session.",
            buttonColor: "#1A855C",
            textColor: "#FFCC33",
        },
        {
            key: "startDrink",
            inactiveLabel: "START DRINK SESSION",
            activeLabel: "START DRINK SESSION!",
            active: !drinkData?.activeDrinkSessionStatus?.hasActiveSession,
            loading: drinkLoading || isStartingDrink,
            onClick: () => setVpModalOpen(true),
            successMessage: "Drink session started!",
            errorMessage: "There was an error starting the drink session.",
            buttonColor: "#8B4513",
            textColor: "#FFCC33",
        },
        {
            key: "startSpoils",
            inactiveLabel: "START SPOILS SESSION",
            activeLabel: "START SPOILS SESSION!",
            active: !spoilsData?.activeSpoilsSessionStatus?.hasActiveSession,
            loading: spoilsLoading || isStartingSpoils,
            onClick: () => setSpoilsVpModalOpen(true),
            successMessage: "Spoils session started!",
            errorMessage: "There was an error starting the spoils session.",
            buttonColor: "#FFCC33",
            textColor: "#000000",
        },
        {
            key: "startClanSpoilsMFG",
            inactiveLabel: "START CLAN SPOILS (MFG)",
            activeLabel: "START CLAN SPOILS (MFG)!",
            active: !clanSpoilsDataMFG?.activeClanSpoilsSessionStatus
                ?.hasActiveSession,
            loading: clanSpoilsLoadingMFG || isStartingClanSpoils,
            onClick: () => {
                setSelectedClanTag("MFG");
                setClanSpoilsVpModalOpen(true);
            },
            successMessage: "Clan spoils session started for MFG!",
            errorMessage:
                "There was an error starting the clan spoils session.",
            buttonColor: "#DC143C",
            textColor: "#FFCC33",
        },
        {
            key: "startClanSpoilsVHV",
            inactiveLabel: "START CLAN SPOILS (VHV)",
            activeLabel: "START CLAN SPOILS (VHV)!",
            active: !clanSpoilsDataVHV?.activeClanSpoilsSessionStatus
                ?.hasActiveSession,
            loading: clanSpoilsLoadingVHV || isStartingClanSpoils,
            onClick: () => {
                setSelectedClanTag("VHV");
                setClanSpoilsVpModalOpen(true);
            },
            successMessage: "Clan spoils session started for VHV!",
            errorMessage:
                "There was an error starting the clan spoils session.",
            buttonColor: "#228B22",
            textColor: "#FFCC33",
        },
        {
            key: "startClanSpoilsNSD",
            inactiveLabel: "START CLAN SPOILS (NSD)",
            activeLabel: "START CLAN SPOILS (NSD)!",
            active: !clanSpoilsDataNSD?.activeClanSpoilsSessionStatus
                ?.hasActiveSession,
            loading: clanSpoilsLoadingNSD || isStartingClanSpoils,
            onClick: () => {
                setSelectedClanTag("NSD");
                setClanSpoilsVpModalOpen(true);
            },
            successMessage: "Clan spoils session started for NSD!",
            errorMessage:
                "There was an error starting the clan spoils session.",
            buttonColor: "#4B0082",
            textColor: "#FFCC33",
        },
        {
            key: "startClanSpoilsASW",
            inactiveLabel: "START CLAN SPOILS (ASW)",
            activeLabel: "START CLAN SPOILS (ASW)!",
            active: !clanSpoilsDataASW?.activeClanSpoilsSessionStatus
                ?.hasActiveSession,
            loading: clanSpoilsLoadingASW || isStartingClanSpoils,
            onClick: () => {
                setSelectedClanTag("ASW");
                setClanSpoilsVpModalOpen(true);
            },
            successMessage: "Clan spoils session started for ASW!",
            errorMessage:
                "There was an error starting the clan spoils session.",
            buttonColor: "#FF8C00",
            textColor: "#000000",
        },
    ];

    return (
        <>
            <div className="bg-brand_charcoal/60 grid w-full grid-cols-1 gap-4 rounded-xl p-6 sm:grid-cols-2 lg:grid-cols-3">
                {buttons.map((btn) => {
                    const isActive = btn.active && !btn.loading;
                    return (
                        <button
                            key={btn.key}
                            className={classNamesExtended(
                                "rounded-lg px-4 py-6 text-center text-xl font-semibold transition-colors duration-300",
                                {
                                    "cursor-pointer": isActive,
                                    "cursor-not-allowed": !isActive,
                                },
                            )}
                            style={{
                                backgroundColor: isActive
                                    ? btn.buttonColor
                                    : "#555",
                                color: isActive ? btn.textColor : "#ccc",
                            }}
                            disabled={!isActive}
                            onClick={btn.onClick}
                        >
                            {btn.loading
                                ? "Processing..."
                                : isActive
                                  ? btn.activeLabel
                                  : btn.inactiveLabel}
                        </button>
                    );
                })}
            </div>

            <FeedbackModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title={modalTitle}
                message={modalMessage}
                isError={modalIsError}
            />

            <VPInputModal
                isOpen={vpModalOpen}
                onClose={() => setVpModalOpen(false)}
                onConfirm={handleStartDrinkSession}
                title="Start Drink Session"
                isLoading={isStartingDrink}
            />

            <VPInputModal
                isOpen={spoilsVpModalOpen}
                onClose={() => setSpoilsVpModalOpen(false)}
                onConfirm={handleStartSpoilsSession}
                title="Start Spoils Session"
                isLoading={isStartingSpoils}
            />

            <VPInputModal
                isOpen={clanSpoilsVpModalOpen}
                onClose={() => setClanSpoilsVpModalOpen(false)}
                onConfirm={handleStartClanSpoilsSession}
                title={`Start Clan Spoils Session (${selectedClanTag})`}
                isLoading={isStartingClanSpoils}
            />
        </>
    );
};

export default ModeratorStreamDeckPanel;
